import { useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import {
    listWorkers,
    loadMoreWorkers,
    reloadWorkers
} from "../Actions/API/WorkerActions"
import { APIWorker } from "../Models/API/Worker"
import { FetchState, hasValue } from "../Models/Client/FetchState"
import { hasOrgPermission } from "../Models/Client/OrgPermissionScope"
import { getItemList, getPageToFetch } from "../Models/Client/RemoteData"
import { SortExpression } from "../Models/Client/SortExpression"
import { Result } from "../Models/Utility/Result"
import { MainThunkDispatch } from "../Reducers"
import { useMyOrgRole } from "./useMyOrgRole"
import { useAppState } from "./useReduxState"

export function useWorkerList(
    orgUsername: string,
    sort: SortExpression<APIWorker>
): [
    FetchState<{
        items: APIWorker[]
        total: number
    }>,
    () => Promise<void>,
    () => Promise<void>
] {
    const entry = useAppState(state => state.api.workers.org[orgUsername])
    const dispatch = useDispatch<MainThunkDispatch>()
    const orgRoleFetch = useMyOrgRole(orgUsername)
    const handleLoadMore = useCallback(
        () => dispatch(loadMoreWorkers(orgUsername)),
        [dispatch, orgUsername]
    )
    const handleReload = useCallback(
        () => dispatch(reloadWorkers(orgUsername)),
        [dispatch, orgUsername]
    )

    useEffect(() => {
        if (
            !hasValue(orgRoleFetch) ||
            !hasOrgPermission(orgRoleFetch.value, "worker:view")
        ) {
            return
        }

        const page = entry === undefined ? 0 : getPageToFetch(entry, sort)

        if (
            page >= 0 ||
            JSON.stringify(sort) !== JSON.stringify(entry?.list?.params)
        ) {
            dispatch(listWorkers(orgUsername, page, sort))
        }
    }, [dispatch, orgUsername, entry, sort, orgRoleFetch])

    return [
        useMemo(() => {
            if (entry === undefined || entry.list === undefined) {
                return undefined
            }

            return Result.ok({
                items: getItemList(entry),
                total: entry.list.total
            })
        }, [entry]),
        handleLoadMore,
        handleReload
    ]
}
